import ExcelJS from "exceljs";

const THIN = { style: "thin", color: { argb: "FFD1D5DB" } };
const LIGHT_BORDER = {
  top: THIN,
  bottom: THIN,
  left: THIN,
  right: THIN,
};

function styleHeader(cell) {
  cell.font = { bold: true, color: { argb: "FF1F2937" }, size: 10 };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE8EEF7" },
  };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = LIGHT_BORDER;
}

function styleData(cell, { align = "left", wrap = false } = {}) {
  cell.font = { size: 10, color: { argb: "FF111827" } };
  cell.alignment = { vertical: "middle", horizontal: align, wrapText: wrap };
  cell.border = LIGHT_BORDER;
}

function parseFailedSummaryLine(line) {
  const m = String(line || "").trim().match(/^([^:]+?)(?:\s*\(([^)]*)\))?\s*:\s*(.+)$/);
  if (!m) return null;
  return {
    empCode: String(m[1] || "").trim() || "—",
    name: String(m[2] || "").trim(),
    message: String(m[3] || "").trim(),
  };
}

/** Merge API/socket errors with optional failedSummary text (legacy / message-only rows). */
export function resolveReimbSyncErrors(errors = [], failedSummary = null) {
  const parsed = String(failedSummary || "")
    .split("\n")
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .map(parseFailedSummaryLine)
    .filter(Boolean);

  return (Array.isArray(errors) ? errors : []).map((e, i) => {
    const p = parsed[i];
    if (e == null) return p ? { ...p } : null;
    if (typeof e === "string") {
      return p
        ? { empCode: p.empCode, name: p.name, message: p.message || e.trim() }
        : { message: e.trim() };
    }
    const code = String(e.empCode || e.employeeCode || e.emp_code || "").trim();
    const name = String(e.name || e.employeeName || "").trim();
    const needs = !code || code === "—" || !name;
    if (needs && p) {
      return {
        ...e,
        empCode: p.empCode && p.empCode !== "—" ? p.empCode : code || "—",
        name: p.name || name,
        message: String(e.message || e.error || p.message || "Failed").trim(),
      };
    }
    return e;
  }).filter(Boolean);
}

export function normalizeReimbSyncFailRows(errors = [], failedSummary = null) {
  const resolved = resolveReimbSyncErrors(errors, failedSummary);
  return resolved.map((e, idx) => {
    if (e == null) {
      return { sl: idx + 1, empCode: "—", name: "—", ctc: null, error: "Failed", code: "" };
    }
    if (typeof e === "string") {
      return { sl: idx + 1, empCode: "—", name: "—", ctc: null, error: e.trim() || "Failed", code: "" };
    }
    const empCode = String(
      e.empCode || e.employeeCode || e.emp_code || e.employee_code || ""
    ).trim();
    const name = String(
      e.name || e.employeeName || e.employee_name || e.userName || ""
    ).trim();
    const ctcRaw =
      e.ctc ??
      e.targetAnnualCtc ??
      e.target_annual_ctc ??
      e.annualCtc ??
      e.currentCtc ??
      null;
    const ctc =
      ctcRaw != null && ctcRaw !== "" && Number.isFinite(Number(ctcRaw))
        ? Number(ctcRaw)
        : null;
    return {
      sl: idx + 1,
      empCode: empCode || (e.employeeId != null ? String(e.employeeId) : "—"),
      name: name || "—",
      ctc,
      error: String(e.message || e.error || e.errorDetail || "Failed").trim(),
      code: e.code || "",
    };
  });
}

export function formatCtcInr(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n));
}

/**
 * Excel export — light header fill, thin borders, clear columns.
 */
export async function downloadReimbSyncFailExcel({
  errors = [],
  meta = {},
  filename,
} = {}) {
  const rows = normalizeReimbSyncFailRows(errors, meta.failedSummary);
  if (!rows.length) throw new Error("No failed rows to export");

  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  const ws = wb.addWorksheet("Sync Failures", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  ws.columns = [
    { header: "Sl No", key: "sl", width: 8 },
    { header: "Employee Code", key: "empCode", width: 16 },
    { header: "Employee Name", key: "name", width: 28 },
    { header: "Current CTC (Annual)", key: "ctc", width: 20 },
    { header: "Error Detail", key: "error", width: 72 },
  ];

  const headerRow = ws.getRow(1);
  headerRow.height = 22;
  headerRow.eachCell((cell) => styleHeader(cell));

  rows.forEach((r) => {
    const row = ws.addRow({
      sl: r.sl,
      empCode: r.empCode,
      name: r.name,
      ctc: r.ctc != null ? r.ctc : "",
      error: r.error,
    });
    row.height = 18;
    styleData(row.getCell(1), { align: "center" });
    styleData(row.getCell(2), { align: "left" });
    styleData(row.getCell(3), { align: "left" });
    styleData(row.getCell(4), { align: "right" });
    if (r.ctc != null) {
      row.getCell(4).numFmt = "₹#,##0";
    }
    styleData(row.getCell(5), { align: "left", wrap: true });
    const errLen = String(r.error || "").length;
    if (errLen > 80) row.height = Math.min(60, 18 + Math.ceil(errLen / 60) * 12);
  });

  // Summary sheet
  const sum = wb.addWorksheet("Summary");
  sum.columns = [
    { header: "Field", key: "k", width: 28 },
    { header: "Value", key: "v", width: 40 },
  ];
  styleHeader(sum.getRow(1).getCell(1));
  styleHeader(sum.getRow(1).getCell(2));
  const summaryRows = [
    ["Report", "Reimbursement structure sync — failures"],
    ["Effective from", meta.effectiveFrom || "—"],
    ["Updated", meta.updated ?? "—"],
    ["Skipped", meta.skipped ?? "—"],
    ["Failed", meta.failed ?? rows.length],
    ["Exported at", new Date().toLocaleString("en-IN")],
  ];
  summaryRows.forEach(([k, v]) => {
    const row = sum.addRow({ k, v: String(v) });
    styleData(row.getCell(1));
    styleData(row.getCell(2));
  });

  const buf = await wb.xlsx.writeBuffer();
  const stamp = new Date().toISOString().slice(0, 10);
  const name =
    filename ||
    `Structure_Reimb_Sync_Failures_${stamp}.xlsx`;
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
